import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useBrand } from "@/context/BrandContext";
import { colorFor, CATEGORY_COLORS } from "@/lib/charts";
import { PageHeader } from "@/components/common/PageHeader";
import { PeriodFilter } from "@/components/common/PeriodFilter";
import { StatCard } from "@/components/common/StatCard";
import { ChartCard } from "@/components/common/ChartCard";
import { Skeleton } from "@/components/ui/skeleton";

export default function OperationalDashboard() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const [period, setPeriod] = useState({ preset: "this_month" });

  const { data, isLoading } = useQuery({
    queryKey: ["dash-operational", brandParam, period.desde, period.hasta],
    queryFn: async () => (await api.get("/dashboards/operational", {
      params: { brand: brandParam, desde: period.desde, hasta: period.hasta },
    })).data,
  });

  if (isLoading || !data) {
    return (
      <div className="p-6"><div className="grid gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-[10px]" />)}
      </div></div>
    );
  }

  return (
    <>
      <PageHeader testId="dash-operational-header" title={t("dashboards.operational.title")}
        subtitle={t("dashboards.operational.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="dash-operational-page">
        <div className="mb-4"><PeriodFilter value={period} onChange={setPeriod} /></div>

        <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard testId="dash-op-open" label={t("dashboards.operational.open_orders")} value={data.ordenes_abiertas} />
          <StatCard testId="dash-op-overdue" label={t("dashboards.operational.overdue")} value={data.ordenes_vencidas} />
          <StatCard testId="dash-op-waiting" label={t("dashboards.operational.waiting_approval")} value={data.esperando_aprobacion} />
          <StatCard testId="dash-op-avg-close" label={t("dashboards.operational.avg_close_time")} value={`${data.tiempo_promedio_cierre_horas} h`} />
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <ChartCard testId="dash-op-by-status" title={t("dashboards.operational.by_status")} height={220} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.por_estado.map((s) => (
                <tr key={s.status} className="border-b border-line">
                  <td className="px-2 py-1">{t(`woStatus.${s.status}`) || s.status}</td><td className="px-2 py-1 text-right">{s.cantidad}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={[Object.fromEntries(data.por_estado.map((s) => [s.status, s.cantidad]))]} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" hide />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {data.por_estado.map((s) => (
                  <Bar isAnimationActive={false} key={s.status} dataKey={s.status} stackId="a" minPointSize={2} fill={colorFor(s.status)} radius={4} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-op-created-closed" title={t("dashboards.operational.created_vs_closed")} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.creadas_vs_cerradas_semanal.map((s) => (
                <tr key={s.semana} className="border-b border-line">
                  <td className="px-2 py-1">{s.semana}</td><td className="px-2 py-1 text-right">{s.creadas}</td><td className="px-2 py-1 text-right">{s.cerradas}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.creadas_vs_cerradas_semanal}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="semana" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line isAnimationActive={false} type="monotone" dataKey="creadas" name={t("dashboards.operational.created")} stroke={CATEGORY_COLORS[0]} strokeWidth={2} dot={false} />
                <Line isAnimationActive={false} type="monotone" dataKey="cerradas" name={t("dashboards.operational.closed")} stroke={CATEGORY_COLORS[2]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-op-sub-load" title={t("dashboards.operational.sub_load")} height={Math.max(180, data.carga_por_sub.length * 30)} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.carga_por_sub.map((s) => (
                <tr key={s.sub_nombre} className="border-b border-line">
                  <td className="px-2 py-1">{s.sub_nombre}</td><td className="px-2 py-1 text-right">{s.activas}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={Math.max(180, data.carga_por_sub.length * 30)}>
              <BarChart data={data.carga_por_sub} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" dataKey="sub_nombre" width={95} tick={{ fontSize: 10.5, fill: "var(--ink-secondary)" }} />
                <Tooltip />
                <Bar isAnimationActive={false} dataKey="activas" fill={CATEGORY_COLORS[0]} radius={4} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-op-overdue-buildings" title={t("dashboards.operational.overdue_buildings")} height={Math.max(180, data.edificios_mas_vencidas.length * 30)} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.edificios_mas_vencidas.map((e) => (
                <tr key={e.property_nombre} className="border-b border-line">
                  <td className="px-2 py-1">{e.property_nombre}</td><td className="px-2 py-1 text-right">{e.vencidas}</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={Math.max(180, data.edificios_mas_vencidas.length * 30)}>
              <BarChart data={data.edificios_mas_vencidas} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" dataKey="property_nombre" width={95} tick={{ fontSize: 10.5, fill: "var(--ink-secondary)" }} />
                <Tooltip />
                <Bar isAnimationActive={false} dataKey="vencidas" fill="var(--state-critical)" radius={4} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard testId="dash-op-close-time-service" title={t("dashboards.operational.close_time_by_service")} height={Math.max(180, data.tiempo_cierre_por_servicio.length * 30)} tableView={
            <table className="w-full text-[12px]"><tbody>
              {data.tiempo_cierre_por_servicio.map((s) => (
                <tr key={s.servicio} className="border-b border-line">
                  <td className="px-2 py-1">{s.servicio}</td><td className="px-2 py-1 text-right">{s.horas_promedio} h</td>
                </tr>
              ))}
            </tbody></table>
          }>
            <ResponsiveContainer width="100%" height={Math.max(180, data.tiempo_cierre_por_servicio.length * 30)}>
              <BarChart data={data.tiempo_cierre_por_servicio} layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "var(--ink-tertiary)" }} />
                <YAxis type="category" dataKey="servicio" width={115} tick={{ fontSize: 10.5, fill: "var(--ink-secondary)" }} />
                <Tooltip formatter={(v) => `${v} h`} />
                <Bar isAnimationActive={false} dataKey="horas_promedio" fill={CATEGORY_COLORS[0]} radius={4} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}
